import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnChanges,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ModalData } from '@shared/abstraction/modal/modal-data';
import { ModalOptions } from '@shared/abstraction/modal/modal-options';
import { ImportFileModalComponent } from '@shared/components/import-file-modal/import-file-modal.component';
import { ImportResultModalComponent } from '@shared/components/import-result-modal/import-result-modal.component';
import { FileImportStatus } from '@shared/constants/file-import-status';
import {
  paginationOptions,
  paginationSettings,
} from '@shared/constants/pagination-settings';
import { NotificationStatus } from '@shared/enums/notification-status';
import { parseTo } from '@shared/helpers/utils';
import {
  AppNotificationService,
  CurrentUserService,
  ModalService,
} from '@shared/services';
import { SidebarItemListService } from '@shared/services/sidebar-item.service';
import moment from 'moment';
import { Observable } from 'rxjs';
import { Collection, Country } from 'src/app/modules/admin/models';
import {
  FileImportResponse,
  FileParameter,
  FileResponse,
} from 'src/app/web-api-client';
import { TaxNews } from '../../models/tax-news';
import { CountryImportService } from '../../services/country-import.service';
import { CountryInformationService } from '../../services/country-information.service';
import { TaxNewsService } from '../../services/tax-news.service';
import { PaginationSetting } from '@shared/modules/grid/abstractions';
import { GridFooterComponent } from '@shared/modules/grid/components/grid-footer/grid-footer.component';

@Component({
  standalone: false,
  selector: 'gstar-tax-news',
  templateUrl: './tax-news.component.html',
  styleUrl: './tax-news.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxNewsComponent implements OnChanges, OnInit {
  private readonly taxNewsService = inject(TaxNewsService);
  private readonly countryInformationService = inject(
    CountryInformationService
  );
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly sidebarService = inject(SidebarItemListService);
  private readonly translateService = inject(TranslateService);
  private readonly importService = inject(CountryImportService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly modalService = inject(ModalService);
  private readonly currentUserService = inject(CurrentUserService);

  countryId = input.required<number>();
  country: WritableSignal<Country> = signal({} as Country);
  taxNews: WritableSignal<TaxNews[]> = signal([]);

  currentPage: number = 1;
  itemsPerPage: number = 10;
  options: KeyValue<string, number>[] = paginationOptions;
  pagination: PaginationSetting = paginationSettings;
  paginationComponent = viewChild<GridFooterComponent<TaxNews>>('footer');
  importLoading: WritableSignal<boolean> = signal<boolean>(false);
  canEditCountry: Signal<boolean> = computed(() =>
    this.currentUserService.canEditCountry(this.countryId())
  );
  showReadOnlyLabel: Signal<boolean> = computed(
    () => this.currentUserService.isCountryAdmin() && !this.canEditCountry()
  );

  dateForm = this.formBuilder.group({
    dateField: {} as Date[],
  });

  get skip(): number {
    return (
      (this.currentPage === 0 ? 0 : this.currentPage - 1) * this.itemsPerPage
    );
  }

  get top(): number {
    return this.itemsPerPage;
  }

  get dateField(): AbstractControl<Date[]> {
    return parseTo<AbstractControl>(this.dateForm.get('dateField'));
  }

  get isDateDisabled(): boolean {
    return this.taxNews().length === 0 && this.dateField.value.length === 0;
  }

  destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.sidebarService.hasCountryChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hasChanged: boolean) => {
        if (hasChanged) {
          this.taxNewsService.lastNewsPage.set(1);
          this.sidebarService.hasCountryChanged.set(false);
        }
      });

    this.dateField.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((date: Date[] | null) => {
        if (date && date?.length > 0) {
          this.paginationComponent()?.goToPage(1);
          this.pagination.currentPage = 1;
          this.currentPage = 1;
          this.taxNewsService.updateQuery({
            skip: this.skip,
            filter: {
              newsDate: {
                ge: moment(date[0])
                  .set({
                    hour: 0,
                    minute: 0,
                    second: 0,
                  })
                  .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                le: moment(date[1])
                  .set({
                    hour: 23,
                    minute: 59,
                    second: 59,
                  })
                  .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
              },
            },
          });
        } else {
          this.clearDate();
        }
        this.fetchNews();
      });

    this.importService.fileImport
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fileImport: FileImportResponse) => {
        if (this.importLoading()) {
          this.processFileImportStatus(fileImport);
        }
      });
  }

  ngOnChanges(): void {
    this.countryInformationService
      .getCountryById(this.countryId())
      .subscribe((country: Country) => {
        this.dateForm.get('dateField')?.setValue({} as Date);
        this.country.set(country);
      });

    this.clearDate();
    this.fetchNews();

    this.pagination.currentPage = this.taxNewsService.lastNewsPage() ?? 1;
    this.currentPage = this.taxNewsService.lastNewsPage() ?? 1;
    this.pageChange({
      currentPage: this.currentPage,
      rowByPage: this.itemsPerPage,
      totalItems: this.pagination.totalItems,
    });
  }

  clearDate(): void {
    this.taxNewsService.updateQuery({
      filter: undefined,
    });
  }

  goToDetails(countryTaxNewsId: number): void {
    if (!this.canEditCountry()) {
      this.router.navigate([
        '/country',
        this.countryId(),
        'tax-news',
        'edit',
        countryTaxNewsId,
      ]);
    }
  }

  pageChange(settings: PaginationSetting): void {
    if (!settings.isFirst) {
      this.pagination = {
        currentPage: settings.currentPage,
        rowByPage: settings.rowByPage,
        totalItems: settings.totalItems,
      };

      this.taxNewsService.lastNewsPage.set(settings.currentPage);
    } else {
      this.pagination = {
        currentPage: this.taxNewsService.lastNewsPage() ?? 1,
        rowByPage: settings.rowByPage,
        totalItems: settings.totalItems,
      };
    }
    this.itemsPerPage = settings.rowByPage;
    this.currentPage = this.taxNewsService.lastNewsPage() ?? 1;
    this.paginationComponent()?.goToPage(
      this.taxNewsService.lastNewsPage() ?? 1
    );
    this.taxNewsService.updateQuery({
      top: this.top,
      skip: this.skip,
    });

    this.fetchNews();
  }

  fetchNews(): void {
    this.taxNewsService
      .getTaxNewsByCountry(this.countryId())
      .subscribe((res: Collection<TaxNews>) => {
        this.taxNews.set(res.items);
        this.pagination.totalItems = res.total;
      });
  }

  addTaxNews(): void {
    if (!this.canEditCountry()) {
      return;
    }
    this.router.navigate([`/country/${this.countryId()}/tax-news/new`]);
  }

  importFile(): void {
    if (!this.canEditCountry()) {
      return;
    }
    const modalId: string = 'import-file-modal';
    const modalOptions: ModalOptions = {
      appModalId: modalId,
    };
    const modalInput: ModalData = {
      title: this.translateService.instant('portfolio.import.title'),
      templateFileName: `GSTAR Country Tax News sample file.xlsx`,
      description: this.translateService.instant(
        'countryInformation.import.message'
      ),
      modalId,
      importFn: (file: FileParameter): void => {
        this.importTaxNewsExcel(file);
      },
      downloadTemplateFn: (): Observable<FileResponse> =>
        this.importService.downloadTaxNewsTemplate(),
    };
    this.modalService.open(ImportFileModalComponent, modalOptions, modalInput);
  }

  importTaxNewsExcel(file: FileParameter): void {
    this.importService
      .importTaxNewsExcel(file, this.countryId())
      .subscribe((response: FileImportResponse) => {
        const fileImportId = response.fileImportId ?? 0;

        this.importLoading.set(true);
        this.importService.reloadFileImportStatus(fileImportId);
        this.notify(
          `notifications.fileProcessing`,
          `notifications.fileProcessingDescription`,
          NotificationStatus.SUCCESS
        );
      });
  }

  private notify(
    title: string,
    description: string,
    status: NotificationStatus,
    params?: JSONObject
  ): void {
    this.appNotificationService.notify(title, description, status, params);
  }

  processFileImportStatus(fileImport: FileImportResponse): void {
    const fileImportStatusId = fileImport.fileImportStatus?.fileImportStatusId;
    if (fileImportStatusId === FileImportStatus.Completed) {
      this.openImportResultModal(fileImport.fileName, fileImport.history, true);
      this.fetchNews();
      this.importLoading.set(false);
    } else if (fileImportStatusId === FileImportStatus.Failed) {
      this.openImportResultModal(
        fileImport.fileName,
        fileImport.history,
        false
      );
      this.importLoading.set(false);
      this.importService.reloadFileImportStatus(0);
    } else {
      this.importService.reloadFileImportStatus(fileImport.fileImportId ?? 0);
    }
  }

  openImportResultModal(
    name?: string,
    content?: string,
    success?: boolean
  ): void {
    this.modalService.open(
      ImportResultModalComponent,
      {
        appModalId: 'import-result-modal',
      },
      {
        title: name,
        content,
        success,
        modalTitle: this.translateService.instant(
          'countryInformation.import.importTitleSuccess',
          { fileName: name }
        ),
        modalId: 'import-result-modal',
      }
    );
  }
}
